import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAdmin } from '../contexts/AdminContext';
import { useApp } from '../contexts/AppContext';
import { getQuoteCategories, getTopicsByCategory, getQuotesByTopic } from '../lib/quoteService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMessageCircle, FiBookOpen, FiUsers, FiTarget, FiHeart, FiGlobe, FiArrowLeft, FiPlus, FiChevronRight } = FiIcons;

const Quotes = () => {
  const navigate = useNavigate();
  const { categoryId, topicId } = useParams();
  const { isAdmin } = useAdmin();
  const { addNotification } = useApp();
  
  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentTopic, setCurrentTopic] = useState(null);

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const categoriesData = await getQuoteCategories();
        setCategories(categoriesData);
        
        // If categoryId is provided, fetch topics for that category
        if (categoryId) {
          const selectedCategory = categoriesData.find(c => c.id === categoryId);
          setCurrentCategory(selectedCategory);
          
          const topicsData = await getTopicsByCategory(categoryId);
          setTopics(topicsData);
          
          // If topicId is provided, fetch quotes for that topic
          if (topicId) {
            const selectedTopic = topicsData.find(t => t.id === topicId);
            setCurrentTopic(selectedTopic);
            
            const quotesData = await getQuotesByTopic(topicId);
            setQuotes(quotesData);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to load quotes data. Please try again.'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [categoryId, topicId, addNotification]);

  // Get icon for category
  const getCategoryIcon = (categoryName) => {
    switch (categoryName?.toLowerCase()) {
      case 'inspirational': return FiHeart;
      case 'educational': return FiBookOpen;
      case 'leadership': return FiTarget;
      case 'personal growth': return FiUsers;
      case 'cultural': return FiGlobe;
      default: return FiMessageCircle;
    }
  };

  // Get color for category
  const getCategoryColor = (categoryName) => {
    switch (categoryName?.toLowerCase()) {
      case 'inspirational': return 'from-pink-500 to-rose-500';
      case 'educational': return 'from-blue-500 to-indigo-500';
      case 'leadership': return 'from-green-500 to-emerald-500';
      case 'personal growth': return 'from-yellow-500 to-amber-500';
      case 'cultural': return 'from-purple-500 to-violet-500';
      default: return 'from-primary-500 to-accent-500';
    }
  };

  const handleCategoryClick = (category) => {
    navigate(`/quotes/${category.id}`);
  };

  const handleTopicClick = (topic) => {
    navigate(`/quotes/${categoryId}/${topic.id}`);
  };

  const handleBackClick = () => {
    if (topicId) {
      navigate(`/quotes/${categoryId}`);
    } else if (categoryId) {
      navigate('/quotes');
    } else {
      navigate('/');
    }
  };

  const handleAddNewClick = () => {
    if (topicId && isAdmin) {
      navigate(`/quotes/admin/add/${topicId}`);
    } else if (categoryId && isAdmin) {
      navigate(`/quotes/admin/topic/add/${categoryId}`);
    } else if (isAdmin) {
      navigate('/quotes/admin/category/add');
    } else {
      addNotification({
        type: 'info',
        title: 'Admin Access Required',
        message: 'Only administrators can add new quotes, topics, or categories.'
      });
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Render quotes for a specific topic
  if (topicId && currentTopic) {
    return (
      <div className="space-y-6 pb-16 px-4">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
        >
          <div className="flex items-center mb-4">
            <button 
              onClick={handleBackClick}
              className="p-2 mr-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h2 className="text-sm text-gray-500">{currentCategory?.name}</h2>
              <h1 className="text-2xl font-bold text-gray-800">{currentTopic.name}</h1>
              <p className="text-sm text-gray-600 mt-1">{currentTopic.description}</p>
            </div>
          </div>
          
          {isAdmin && (
            <button 
              onClick={handleAddNewClick}
              className="flex items-center space-x-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>Add New Quote</span>
            </button>
          )}
        </motion.div>

        {/* Quotes List */}
        <div className="space-y-4">
          {quotes.length > 0 ? (
            quotes.map((quote, index) => (
              <motion.div 
                key={quote.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
              >
                <p className="text-gray-800 text-lg italic mb-4">"{quote.text}"</p>
                <div className="flex items-center justify-between">
                  <p className="font-medium text-gray-700">— {quote.author}</p>
                  {isAdmin && (
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => navigate(`/quotes/admin/edit/${quote.id}`)}
                        className="p-2 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <SafeIcon icon={FiIcons.FiEdit2} className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete this quote?')) {
                            // Handle delete logic
                          }
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <SafeIcon icon={FiIcons.FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 text-center"
            >
              <SafeIcon icon={FiMessageCircle} className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No quotes found</h3>
              <p className="text-gray-600 mb-6">This topic doesn't have any quotes yet.</p>
              {isAdmin && (
                <button
                  onClick={handleAddNewClick}
                  className="bg-primary-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-600 transition-colors"
                >
                  Add the first quote
                </button>
              )}
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  // Render topics for a specific category
  if (categoryId && currentCategory) {
    return (
      <div className="space-y-6 pb-16 px-4">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className={`bg-gradient-to-r ${getCategoryColor(currentCategory.name)} rounded-xl shadow-lg p-6 text-white`}
        >
          <div className="flex items-center mb-4">
            <button 
              onClick={handleBackClick}
              className="p-2 mr-3 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">{currentCategory.name} Quotes</h1>
              <p className="text-white text-opacity-90 mt-1">{currentCategory.description}</p>
            </div>
          </div>
          
          {isAdmin && (
            <button 
              onClick={handleAddNewClick}
              className="flex items-center space-x-2 bg-white text-primary-600 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm mt-4"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>Add New Topic</span>
            </button>
          )}
        </motion.div>

        {/* Topics List */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {topics.length > 0 ? (
            topics.map((topic, index) => (
              <motion.div 
                key={topic.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => handleTopicClick(topic)}
                className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 cursor-pointer hover:shadow-xl transition-shadow"
              >
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-bold text-gray-800">{topic.name}</h2>
                  <SafeIcon icon={FiChevronRight} className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-sm text-gray-600 mt-1">{topic.description}</p>
              </motion.div>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="col-span-2 bg-white rounded-xl shadow-lg p-8 border border-gray-100 text-center"
            >
              <SafeIcon icon={FiMessageCircle} className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No topics found</h3>
              <p className="text-gray-600 mb-6">This category doesn't have any topics yet.</p>
              {isAdmin && (
                <button
                  onClick={handleAddNewClick}
                  className="bg-primary-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-600 transition-colors"
                >
                  Add the first topic
                </button>
              )}
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  // Render categories (default view)
  return (
    <div className="space-y-6 pb-16 px-4">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      >
        <div className="text-center">
          <SafeIcon icon={FiMessageCircle} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Quote Categories</h1>
          <p className="text-gray-600">
            Browse through our collection of inspirational and educational quotes
          </p>
        </div>
        
        {isAdmin && (
          <div className="flex justify-center mt-6">
            <button 
              onClick={handleAddNewClick}
              className="flex items-center space-x-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>Add New Category</span>
            </button>
          </div>
        )}
      </motion.div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map((category, index) => (
          <motion.div 
            key={category.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => handleCategoryClick(category)}
            className={`bg-gradient-to-r ${getCategoryColor(category.name)} rounded-xl shadow-lg p-6 text-white cursor-pointer hover:shadow-xl transition-shadow`}
          >
            <div className="flex justify-between items-center mb-4">
              <div className="p-3 bg-white bg-opacity-20 rounded-lg">
                <SafeIcon icon={getCategoryIcon(category.name)} className="w-6 h-6 text-white" />
              </div>
              <SafeIcon icon={FiChevronRight} className="w-5 h-5 text-white text-opacity-70" />
            </div>
            <h2 className="text-xl font-bold">{category.name}</h2>
            <p className="text-white text-opacity-80 mt-1 text-sm">{category.description}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Quotes;